/**
 * BarVisualizer — Frequency-reactive bar visualization for voice input.
 *
 * Shows animated bars driven by audio frequency data.
 * Uses Martha coral as the foreground color.
 */

interface BarVisualizerProps {
  /** Frequency data array (from getInputByteFrequencyData) */
  frequencyData?: Uint8Array;
  /** Fallback single audio level (0-1) when no frequency data available */
  audioLevel?: number;
  /** Number of bars to display (default 5) */
  barCount?: number;
  /** Height of container in px (default 24) */
  height?: number;
}

export function BarVisualizer({
  frequencyData,
  audioLevel = 0,
  barCount = 5,
  height = 24,
}: BarVisualizerProps) {
  const bars = [];

  for (let i = 0; i < barCount; i++) {
    let barHeight: number;

    if (frequencyData && frequencyData.length > 0) {
      // Sample frequency bins evenly across the data
      const binIndex = Math.floor((i / barCount) * frequencyData.length);
      barHeight = (frequencyData[binIndex] / 255) * height;
    } else {
      // Fallback: use audioLevel with some variance per bar
      const variance = Math.sin(Date.now() / 200 + i * 1.2) * 0.3 + 0.7;
      barHeight = audioLevel * height * variance;
    }

    // Minimum visible height
    barHeight = Math.max(3, barHeight);

    bars.push(
      <span
        key={i}
        className="inline-block rounded-full transition-[height] duration-75 ease-out"
        style={{
          width: 3,
          height: barHeight,
          backgroundColor: '#D97F6F',
          opacity: 0.8 + (barHeight / height) * 0.2,
        }}
      />,
    );
  }

  return (
    <div
      className="flex items-end gap-[2px]"
      style={{ height }}
    >
      {bars}
    </div>
  );
}
